/**
 * 年度交易統計服務
 * 提供年度交易數據的計算和分析功能
 */

import { Transaction } from '../types';
import { logger } from '../utils/logger';
import { DecimalHelper } from '../utils/decimalHelper';

// 月度交易統計
export interface MonthlyStats {
  month: number;
  monthName: string;
  buyCount: number;
  sellCount: number;
  dividendCount: number;
  buyAmount: number;
  sellAmount: number;
  realizedGain: number;
  fees: number;
  taxes: number;
}

// 股票交易統計
export interface StockAnnualStats {
  symbol: string;
  name: string;
  buyCount: number;
  sellCount: number;
  buyQuantity: number;
  sellQuantity: number;
  buyAmount: number;
  sellAmount: number;
  realizedGain: number;
  dividends: number;
  fees: number;
  taxes: number;
  avgBuyPrice: number;
  avgSellPrice: number;
}

// 最佳/最差交易
export interface BestWorstTrade {
  symbol: string;
  name: string;
  type: 'best' | 'worst';
  realizedGain: number;
  quantity: number;
  buyPrice: number;
  sellPrice: number;
  transactionDate: Date;
}

// 交易類型統計
export interface TransactionTypeStats {
  type: string;
  count: number;
  amount: number;
  percentage: number;
}

// 年度交易總統計
export interface AnnualTransactionSummary {
  year: number;
  totalTransactions: number;
  buyCount: number;
  sellCount: number;
  dividendCount: number;
  cashDepositCount: number;
  cashWithdrawalCount: number;
  stockIncreaseCount: number;
  stockDecreaseCount: number;
  totalBuyAmount: number;
  totalSellAmount: number;
  totalDividendAmount: number;
  totalCashDeposit: number;
  totalCashWithdrawal: number;
  totalRealizedGain: number;
  totalFees: number;
  totalTaxes: number;
  netProfit: number; // 實現損益 + 股利 - 手續費 - 稅金
  uniqueStocksCount: number;
  monthlyStats: MonthlyStats[];
  stockStats: StockAnnualStats[];
  transactionTypeDistribution: TransactionTypeStats[];
  topGainers: StockAnnualStats[];
  topLosers: StockAnnualStats[];
  bestTrade: BestWorstTrade | null;
  worstTrade: BestWorstTrade | null;
}

export class AnnualStatsService {
  /**
   * 計算指定年份的交易統計
   */
  static calculateAnnualStats(
    transactions: Transaction[],
    year: number
  ): AnnualTransactionSummary {
    annualStatsLogger.debug('開始計算年度交易統計', { year, transactionsCount: transactions.length });

    // 篩選指定年份的交易
    const yearTransactions = transactions.filter(t => {
      const date = t.transactionDate || t.timestamp;
      return date.getFullYear() === year;
    });

    // 計算月度統計
    const monthlyStats = this.calculateMonthlyStats(yearTransactions, year);

    // 計算股票統計
    const stockStats = this.calculateStockStats(yearTransactions);

    // 計算交易類型分布
    const transactionTypeDistribution = this.calculateTypeDistribution(yearTransactions);

    // 計算最佳/最差交易
    const { bestTrade, worstTrade } = this.findBestWorstTrades(yearTransactions);

    // 計算總計數據
    const summary: AnnualTransactionSummary = {
      year,
      totalTransactions: yearTransactions.length,
      buyCount: yearTransactions.filter(t => t.type === 'buy').length,
      sellCount: yearTransactions.filter(t => t.type === 'sell').length,
      dividendCount: yearTransactions.filter(t => t.type === 'dividend').length,
      cashDepositCount: yearTransactions.filter(t => t.type === 'cash_deposit').length,
      cashWithdrawalCount: yearTransactions.filter(t => t.type === 'cash_withdrawal').length,
      stockIncreaseCount: yearTransactions.filter(t => t.type === 'stock_increase').length,
      stockDecreaseCount: yearTransactions.filter(t => t.type === 'stock_decrease').length,
      totalBuyAmount: yearTransactions
        .filter(t => t.type === 'buy')
        .reduce((sum, t) => DecimalHelper.add(sum, t.total), 0),
      totalSellAmount: yearTransactions
        .filter(t => t.type === 'sell')
        .reduce((sum, t) => DecimalHelper.add(sum, t.total), 0),
      totalDividendAmount: yearTransactions
        .filter(t => t.type === 'dividend')
        .reduce((sum, t) => DecimalHelper.add(sum, t.total), 0),
      totalCashDeposit: yearTransactions
        .filter(t => t.type === 'cash_deposit')
        .reduce((sum, t) => DecimalHelper.add(sum, t.total), 0),
      totalCashWithdrawal: yearTransactions
        .filter(t => t.type === 'cash_withdrawal')
        .reduce((sum, t) => DecimalHelper.add(sum, t.total), 0),
      totalRealizedGain: this.calculateTotalRealizedGain(yearTransactions),
      totalFees: yearTransactions.reduce((sum, t) => DecimalHelper.add(sum, t.fees || 0), 0),
      totalTaxes: yearTransactions.reduce((sum, t) => DecimalHelper.add(sum, t.taxes || 0), 0),
      netProfit: 0, // 將在後面計算
      uniqueStocksCount: new Set(yearTransactions.map(t => t.symbol)).size,
      monthlyStats,
      stockStats,
      transactionTypeDistribution,
      topGainers: stockStats.filter(s => s.realizedGain > 0).sort((a, b) => b.realizedGain - a.realizedGain).slice(0, 5),
      topLosers: stockStats.filter(s => s.realizedGain < 0).sort((a, b) => a.realizedGain - b.realizedGain).slice(0, 5),
      bestTrade,
      worstTrade,
    };

    // 計算淨利潤
    // 語意(FIN-03):totalRealizedGain 的賣出總額與 totalDividendAmount 皆為「淨額」
    // (賣出/股利的手續費與稅金已扣除),此處僅需再扣「買入手續費」;
    // 若再扣 totalFees + totalTaxes 會把賣出/股利費用重複扣減兩次
    const buyOnlyFees = yearTransactions
      .filter(t => t.type === 'buy')
      .reduce((sum, t) => DecimalHelper.add(sum, t.fees || 0), 0);
    summary.netProfit = DecimalHelper.subtract(
      DecimalHelper.add(summary.totalRealizedGain, summary.totalDividendAmount),
      buyOnlyFees
    );

    annualStatsLogger.debug('年度交易統計計算完成', { 
      year, 
      totalTransactions: summary.totalTransactions,
      totalRealizedGain: summary.totalRealizedGain 
    });

    return summary;
  }

  /**
   * 計算月度統計
   */
  private static calculateMonthlyStats(
    transactions: Transaction[],
    year: number
  ): MonthlyStats[] {
    const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
    
    const monthlyStats: MonthlyStats[] = [];

    for (let month = 0; month < 12; month++) {
      const monthTransactions = transactions.filter(t => {
        const date = t.transactionDate || t.timestamp;
        return date.getMonth() === month;
      });

      const buyTransactions = monthTransactions.filter(t => t.type === 'buy');
      const sellTransactions = monthTransactions.filter(t => t.type === 'sell');
      const dividendTransactions = monthTransactions.filter(t => t.type === 'dividend');

      // 計算該月已實現損益（簡化計算：賣出總額 - 賣出股數 * 平均買入成本）
      // 這裡使用簡化版本，完整計算需要使用 HoldingsCalculationService
      const realizedGain = this.calculateMonthlyRealizedGain(monthTransactions);

      monthlyStats.push({
        month,
        monthName: monthNames[month],
        buyCount: buyTransactions.length,
        sellCount: sellTransactions.length,
        dividendCount: dividendTransactions.length,
        buyAmount: buyTransactions.reduce((sum, t) => DecimalHelper.add(sum, t.total), 0),
        sellAmount: sellTransactions.reduce((sum, t) => DecimalHelper.add(sum, t.total), 0),
        realizedGain,
        fees: monthTransactions.reduce((sum, t) => DecimalHelper.add(sum, t.fees || 0), 0),
        taxes: monthTransactions.reduce((sum, t) => DecimalHelper.add(sum, t.taxes || 0), 0),
      });
    }

    return monthlyStats;
  }

  /**
   * 計算月度已實現損益
   */
  private static calculateMonthlyRealizedGain(transactions: Transaction[]): number {
    // 簡化計算：只計算有完整買賣配對的損益
    // 實際應用中可能需要更複雜的 FIFO/LIFO 計算
    const stockTransactions = new Map<string, Transaction[]>();

    transactions.forEach(t => {
      if (!stockTransactions.has(t.symbol)) {
        stockTransactions.set(t.symbol, []);
      }
      stockTransactions.get(t.symbol)!.push(t);
    });

    let totalRealizedGain = 0;

    stockTransactions.forEach((unsortedTrans) => {
      let buyQueue: { quantity: number; price: number }[] = [];

      // FIN-02:FIFO 前必須依交易時間升冪排序 —
      // 傳入的交易可能是 timestamp 降冪(新→舊),不排序會把賣出配對到「未來的買進」,
      // 已實現損益恆為 0 或全錯(比照 holdingsCalculationService 的做法)
      const stockTrans = [...unsortedTrans].sort((a, b) => {
        const aTime = (a.transactionDate || a.timestamp)?.getTime() ?? 0;
        const bTime = (b.transactionDate || b.timestamp)?.getTime() ?? 0;
        return aTime - bTime;
      });

      stockTrans.forEach(t => {
        if (t.type === 'buy') {
          buyQueue.push({ quantity: t.quantity, price: t.price });
        } else if (t.type === 'sell') {
          let remainingQuantity = t.quantity;
          let sellTotal = t.total;
          let costTotal = 0;

          while (remainingQuantity > 0 && buyQueue.length > 0) {
            const buy = buyQueue[0];
            const quantityToUse = Math.min(remainingQuantity, buy.quantity);
            costTotal = DecimalHelper.add(costTotal, DecimalHelper.multiply(quantityToUse, buy.price));
            remainingQuantity -= quantityToUse;
            buy.quantity -= quantityToUse;

            if (buy.quantity === 0) {
              buyQueue.shift();
            }
          }

          if (costTotal > 0) {
            const gain = DecimalHelper.subtract(sellTotal, costTotal);
            totalRealizedGain = DecimalHelper.add(totalRealizedGain, gain);
          }
        }
      });
    });

    return totalRealizedGain;
  }

  /**
   * 計算股票統計
   */
  private static calculateStockStats(transactions: Transaction[]): StockAnnualStats[] {
    const stockMap = new Map<string, StockAnnualStats>();

    transactions.forEach(t => {
      if (!stockMap.has(t.symbol)) {
        stockMap.set(t.symbol, {
          symbol: t.symbol,
          name: t.stockName,
          buyCount: 0,
          sellCount: 0,
          buyQuantity: 0,
          sellQuantity: 0,
          buyAmount: 0,
          sellAmount: 0,
          realizedGain: 0,
          dividends: 0,
          fees: 0,
          taxes: 0,
          avgBuyPrice: 0,
          avgSellPrice: 0,
        });
      }

      const stats = stockMap.get(t.symbol)!;

      switch (t.type) {
        case 'buy':
          stats.buyCount++;
          stats.buyQuantity = DecimalHelper.add(stats.buyQuantity, t.quantity);
          stats.buyAmount = DecimalHelper.add(stats.buyAmount, t.total);
          stats.fees = DecimalHelper.add(stats.fees, t.fees || 0);
          break;
        case 'sell':
          stats.sellCount++;
          stats.sellQuantity = DecimalHelper.add(stats.sellQuantity, t.quantity);
          stats.sellAmount = DecimalHelper.add(stats.sellAmount, t.total);
          stats.taxes = DecimalHelper.add(stats.taxes, t.taxes || 0);
          stats.fees = DecimalHelper.add(stats.fees, t.fees || 0);
          break;
        case 'dividend':
          stats.dividends = DecimalHelper.add(stats.dividends, t.total);
          break;
      }
    });

    // 計算平均價格和已實現損益
    const result = Array.from(stockMap.values()).map(stats => {
      stats.avgBuyPrice = stats.buyQuantity > 0 
        ? DecimalHelper.divide(stats.buyAmount, stats.buyQuantity) 
        : 0;
      stats.avgSellPrice = stats.sellQuantity > 0 
        ? DecimalHelper.divide(stats.sellAmount, stats.sellQuantity) 
        : 0;
      
      // 簡化的已實現損益計算
      if (stats.sellQuantity > 0 && stats.buyQuantity > 0) {
        const sellCost = DecimalHelper.multiply(stats.sellQuantity, stats.avgBuyPrice);
        stats.realizedGain = DecimalHelper.subtract(stats.sellAmount, sellCost);
      }

      return stats;
    });

    return result;
  }

  /**
   * 計算交易類型分布
   */
  private static calculateTypeDistribution(transactions: Transaction[]): TransactionTypeStats[] {
    const typeNames: { [key: string]: string } = {
      buy: '買入',
      sell: '賣出',
      dividend: '股利',
      cash_deposit: '現金存入',
      cash_withdrawal: '現金提領',
      stock_increase: '股票增額',
      stock_decrease: '股票減額',
    };

    const typeMap = new Map<string, { count: number; amount: number }>();
    const totalCount = transactions.length;

    transactions.forEach(t => {
      const current = typeMap.get(t.type) || { count: 0, amount: 0 };
      typeMap.set(t.type, {
        count: current.count + 1,
        amount: DecimalHelper.add(current.amount, t.total),
      });
    });

    return Array.from(typeMap.entries()).map(([type, data]) => ({
      type: typeNames[type] || type,
      count: data.count,
      amount: data.amount,
      percentage: totalCount > 0 ? (data.count / totalCount) * 100 : 0,
    }));
  }

  /**
   * 計算總已實現損益
   */
  private static calculateTotalRealizedGain(transactions: Transaction[]): number {
    return this.calculateMonthlyRealizedGain(transactions);
  }

  /**
   * 找出最佳和最差交易
   */
  private static findBestWorstTrades(transactions: Transaction[]): { 
    bestTrade: BestWorstTrade | null; 
    worstTrade: BestWorstTrade | null 
  } {
    // 按股票分組計算每筆交易的損益
    const trades: BestWorstTrade[] = [];
    const stockTransactions = new Map<string, Transaction[]>();

    transactions.forEach(t => {
      if (t.type === 'buy' || t.type === 'sell') {
        if (!stockTransactions.has(t.symbol)) {
          stockTransactions.set(t.symbol, []);
        }
        stockTransactions.get(t.symbol)!.push(t);
      }
    });

    stockTransactions.forEach((unsortedTrans, symbol) => {
      let buyQueue: { quantity: number; price: number; date: Date }[] = [];

      // FIN-02:同 calculateMonthlyRealizedGain — FIFO 前依交易時間升冪排序
      const stockTrans = [...unsortedTrans].sort((a, b) => {
        const aTime = (a.transactionDate || a.timestamp)?.getTime() ?? 0;
        const bTime = (b.transactionDate || b.timestamp)?.getTime() ?? 0;
        return aTime - bTime;
      });

      stockTrans.forEach(t => {
        const date = t.transactionDate || t.timestamp;

        if (t.type === 'buy') {
          buyQueue.push({ quantity: t.quantity, price: t.price, date });
        } else if (t.type === 'sell') {
          let remainingQuantity = t.quantity;

          while (remainingQuantity > 0 && buyQueue.length > 0) {
            const buy = buyQueue[0];
            const quantityToUse = Math.min(remainingQuantity, buy.quantity);
            const cost = DecimalHelper.multiply(quantityToUse, buy.price);
            const revenue = DecimalHelper.multiply(quantityToUse, t.price);
            const realizedGain = DecimalHelper.subtract(revenue, cost);

            trades.push({
              symbol,
              name: t.stockName,
              type: realizedGain >= 0 ? 'best' : 'worst',
              realizedGain,
              quantity: quantityToUse,
              buyPrice: buy.price,
              sellPrice: t.price,
              transactionDate: date,
            });

            remainingQuantity -= quantityToUse;
            buy.quantity -= quantityToUse;

            if (buy.quantity === 0) {
              buyQueue.shift();
            }
          }
        }
      });
    });

    if (trades.length === 0) {
      return { bestTrade: null, worstTrade: null };
    }

    // 找出最佳和最差
    const sortedTrades = trades.sort((a, b) => b.realizedGain - a.realizedGain);
    const bestTrade = sortedTrades[0];
    const worstTrade = sortedTrades[sortedTrades.length - 1];

    return { bestTrade, worstTrade };
  }

  /**
   * 獲取交易年份列表（用於年份選擇器）
   */
  static getAvailableYears(transactions: Transaction[]): number[] {
    const years = new Set<number>();
    
    transactions.forEach(t => {
      const date = t.transactionDate || t.timestamp;
      years.add(date.getFullYear());
    });

    return Array.from(years).sort((a, b) => b - a);
  }
}

// 年度統計專用的日誌工具
const annualStatsLogger = {
  debug: (message: string, data?: any) => logger.debug(`[AnnualStats] ${message}`, data),
  info: (message: string, data?: any) => logger.info(`[AnnualStats] ${message}`, data),
  error: (message: string, data?: any) => logger.error(`[AnnualStats] ${message}`, data),
};
